'use client';

import { useEffect, useRef } from 'react';
import * as PIXI from 'pixi.js';

export interface PixiEffectsRef {
  showNoteSuccess: () => void;
  showAllNotesSuccess: () => void;
  // show a small bubble near a note: position in screen coords, scoreDelta is number (positive -> green, negative -> red)
  showNoteBubble: (x: number, y: number, scoreDelta: number) => void;
  // show practice end big effect
  showPracticeEnd: (scoreDelta?: number) => void;
}

export const usePixiEffects = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const effectsRef = useRef<PixiEffectsRef | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create PIXI Application
    const app = new PIXI.Application();
    
    const initPixi = async () => {
      await app.init({
        width: window.innerWidth,
        height: window.innerHeight,
        backgroundColor: 0x000000,
        backgroundAlpha: 0, // Transparent background
        antialias: true,
      });

      // Make the canvas transparent and positioned absolutely
      app.canvas.style.position = 'fixed';
      app.canvas.style.top = '0';
      app.canvas.style.left = '0';
      app.canvas.style.pointerEvents = 'none'; // Don't block interactions
      app.canvas.style.zIndex = '9999'; // On top of everything
      
      containerRef.current?.appendChild(app.canvas);
      appRef.current = app;

      // Create effects object
      effectsRef.current = {
        showNoteSuccess: () => {
          console.log('🎨 Triggering PIXI note success effect');
          showNoteSuccessEffect(app);
        },
        showAllNotesSuccess: () => {
          console.log('🎨 Triggering PIXI all notes success effect');
          showAllNotesSuccessEffect(app);
        },
        showNoteBubble: (x: number, y: number, scoreDelta: number) => {
          showNoteBubbleEffect(app, x, y, scoreDelta);
        },
        showPracticeEnd: (scoreDelta?: number) => {
          showPracticeEndEffect(app, scoreDelta ?? 0);
        },
      };

      console.log('🎨 PIXI.js initialized successfully', { width: window.innerWidth, height: window.innerHeight });
    };

    initPixi();

    // Handle window resize
    const handleResize = () => {
      if (appRef.current) {
        appRef.current.renderer.resize(window.innerWidth, window.innerHeight);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
      }
    };
  }, []);

  return { containerRef, effects: effectsRef.current };
};

// Effect for individual note success
const showNoteSuccessEffect = (app: PIXI.Application) => {
  // Create a simple green circle that appears and fades out
  const circle = new PIXI.Graphics();
  circle.beginFill(0x00FF00, 0.8); // Green color
  circle.drawCircle(0, 0, 20);
  circle.endFill();

  // Random position on screen
  circle.x = Math.random() * app.screen.width;
  circle.y = Math.random() * app.screen.height;

  app.stage.addChild(circle);

  // Animate the circle
  const startTime = Date.now();
  const duration = 1000; // 1 second

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = elapsed / duration;

    if (progress >= 1) {
      app.stage.removeChild(circle);
      return;
    }

    // Scale up and fade out
    const scale = 1 + progress * 2;
    const alpha = 1 - progress;

    circle.scale.set(scale);
    circle.alpha = alpha;

    requestAnimationFrame(animate);
  };

  animate();
};

// Show a small bubble near a played note with score delta text
const showNoteBubbleEffect = (
  app: PIXI.Application,
  x: number,
  y: number,
  scoreDelta: number,
) => {
  const container = new PIXI.Container();
  container.x = x;
  container.y = y;

  const positive = scoreDelta >= 0;
  const color = positive ? 0x00c853 : 0xff1744; // green / red

  const bubble = new PIXI.Graphics();
  bubble.beginFill(color, 0.95);
  bubble.drawCircle(0, 0, 18);
  bubble.endFill();

  const style = new PIXI.TextStyle({
    fontFamily: 'Arial',
    fontSize: 16,
    fill: 0xffffff,
    stroke: { color: 0x000000, width: 3 },
  });
  const label = new PIXI.Text((positive ? '+' : '') + String(scoreDelta), style);
  label.anchor.set(0.5);
  label.y = 0;

  container.addChild(bubble);
  container.addChild(label);
  container.scale.set(0.6);

  app.stage.addChild(container);

  const start = Date.now();
  const duration = 900;

  const animate = () => {
    const elapsed = Date.now() - start;
    const t = Math.min(1, elapsed / duration);

    // ease out for scale and upward movement
    const ease = 1 - Math.pow(1 - t, 3);
    container.scale.set(0.6 + ease * 1.2);
    container.alpha = 1 - t;
    container.y = y - ease * 36;

    if (t >= 1) {
      app.stage.removeChild(container);
      return;
    }

    requestAnimationFrame(animate);
  };

  animate();
};

// Big practice end 'boom' effect with particles and score popup
const showPracticeEndEffect = (app: PIXI.Application, scoreDelta: number) => {
  const centerX = app.screen.width / 2;
  const centerY = app.screen.height / 2.6;

  const particles: StarGraphics[] = [];
  const count = 40;

  for (let i = 0; i < count; i++) {
    const p = new PIXI.Graphics() as StarGraphics;
    const radius = 6 + Math.random() * 8;
    const hue = Math.floor(200 + Math.random() * 55);
    const color = 0xffd54f; // warm gold
    p.beginFill(color, 0.95);
    p.drawCircle(0, 0, radius);
    p.endFill();

    p.x = centerX;
    p.y = centerY;

    const speed = 3 + Math.random() * 6;
    const angle = Math.random() * Math.PI * 2;
    p.vx = Math.cos(angle) * speed;
    p.vy = Math.sin(angle) * speed - (Math.random() * 1.5);

    p.rotation = Math.random() * Math.PI * 2;
    particles.push(p);
    app.stage.addChild(p);
  }

  // Big text
  const bigStyle = new PIXI.TextStyle({
    fontFamily: 'Arial',
    fontSize: 48,
    fill: 0xffffff,
    stroke: { color: 0x000000, width: 6 },
  });
  const smallStyle = new PIXI.TextStyle({
    fontFamily: 'Arial',
    fontSize: 28,
    fill: scoreDelta >= 0 ? 0x00c853 : 0xff1744,
    stroke: { color: 0x000000, width: 4 },
  });

  const txt = new PIXI.Text('Practice Complete', bigStyle);
  txt.anchor.set(0.5);
  txt.x = centerX;
  txt.y = centerY - 30;
  txt.scale.set(0.6);
  app.stage.addChild(txt);

  const scoreTxt = new PIXI.Text((scoreDelta >= 0 ? '+' : '') + String(scoreDelta), smallStyle);
  scoreTxt.anchor.set(0.5);
  scoreTxt.x = centerX;
  scoreTxt.y = centerY + 30;
  scoreTxt.scale.set(0.8);
  app.stage.addChild(scoreTxt);

  const start = Date.now();
  const duration = 1800;

  const animate = () => {
    const elapsed = Date.now() - start;
    const t = Math.min(1, elapsed / duration);

    // particles
    particles.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.08; // gravity
      p.rotation += 0.05;
      p.alpha = 1 - t;
      p.scale.set(0.8 + t * 0.8);
    });

    // text pop and fade
    const ease = 1 - Math.pow(1 - t, 3);
    txt.scale.set(0.6 + ease * 0.8);
    txt.alpha = 1 - t * 0.2;
    scoreTxt.scale.set(0.8 + ease * 0.6);
    scoreTxt.alpha = 1 - t;

    if (t >= 1) {
      particles.forEach((p) => app.stage.removeChild(p));
      app.stage.removeChild(txt);
      app.stage.removeChild(scoreTxt);
      return;
    }

    requestAnimationFrame(animate);
  };

  animate();
};

// Extended Graphics interface for velocity properties
interface StarGraphics extends PIXI.Graphics {
  vx: number;
  vy: number;
}

// Effect for all notes in measure success
const showAllNotesSuccessEffect = (app: PIXI.Application) => {
  // Create multiple golden stars
  const starCount = 10;
  const stars: StarGraphics[] = [];

  for (let i = 0; i < starCount; i++) {
    const star = new PIXI.Graphics() as StarGraphics;
    star.beginFill(0xFFD700, 0.9); // Gold color
    
    // Draw a simple star shape
    const points = [];
    const outerRadius = 15;
    const innerRadius = 7;
    
    for (let j = 0; j < 10; j++) {
      const angle = (j * Math.PI) / 5;
      const radius = j % 2 === 0 ? outerRadius : innerRadius;
      points.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
    }
    
    star.drawPolygon(points);
    star.endFill();

    // Position stars randomly across the screen
    star.x = Math.random() * app.screen.width;
    star.y = Math.random() * app.screen.height;
    
    // Random initial velocity
    star.vx = (Math.random() - 0.5) * 4;
    star.vy = (Math.random() - 0.5) * 4;

    stars.push(star);
    app.stage.addChild(star);
  }

  // Animate stars
  const startTime = Date.now();
  const duration = 2000; // 2 seconds

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = elapsed / duration;

    if (progress >= 1) {
      stars.forEach(star => app.stage.removeChild(star));
      return;
    }

    stars.forEach(star => {
      // Move stars
      star.x += star.vx;
      star.y += star.vy;

      // Rotate stars
      star.rotation += 0.1;

      // Fade out over time
      star.alpha = 1 - progress;

      // Scale effect
      const scaleEffect = 1 + Math.sin(progress * Math.PI) * 0.5;
      star.scale.set(scaleEffect);
    });

    requestAnimationFrame(animate);
  };

  animate();
};